/**
 * Maps Supabase OAuth callback query params to user-facing banner text.
 */

import { ERROR_MESSAGES } from '../constants/oauth.constants'

export type OAuthCallbackErrorParams = {
  error: string
  errorCode?: string | null
  errorDescription?: string | null
}

const OAUTH_CODE_REUSE_HINTS = [
  'unable to exchange external code',
  'invalid_grant',
  'exchange external code',
] as const

export const OAUTH_CALLBACK_CODE_REUSED_MESSAGE =
  'Sign-in link expired or was already used. Please click your sign-in button again.'

function normalizeOAuthErrorDescription(description: string | null | undefined): string {
  if (!description?.trim()) return ''
  try {
    return decodeURIComponent(description.replace(/\+/g, ' ')).toLowerCase()
  } catch {
    return description.replace(/\+/g, ' ').toLowerCase()
  }
}

export function isOAuthCodeReuseFailure(params: OAuthCallbackErrorParams): boolean {
  const { error, errorCode, errorDescription } = params
  const normalizedError = error.toLowerCase()
  if (normalizedError === 'server_error' || normalizedError === 'temporarily_unavailable') {
    const desc = normalizeOAuthErrorDescription(errorDescription)
    if (OAUTH_CODE_REUSE_HINTS.some((hint) => desc.includes(hint))) {
      return true
    }
  }
  const normalizedCode = (errorCode ?? '').toLowerCase()
  return normalizedCode === 'invalid_grant' || normalizedCode === 'bad_oauth_state'
}

/**
 * User-facing banner for OAuth callback failures surfaced on /sign-in after redirect.
 */
export function resolveOAuthCallbackBannerMessage(params: OAuthCallbackErrorParams): string {
  const { error } = params
  if (isOAuthCodeReuseFailure(params)) {
    return OAUTH_CALLBACK_CODE_REUSED_MESSAGE
  }
  const friendly = ERROR_MESSAGES[error]
  return friendly?.message ?? 'Sign-in failed. Please try again.'
}
